/**
 * Opt-in reload diagnostic — helps pin down why a page reloads on devices where
 * devtools aren't available (notably iOS Safari). It is OFF for everyone by
 * default and changes no app behavior.
 *
 * Turn it on by visiting any URL with `?diag=1` once; it then persists per
 * device (localStorage) across reloads and in-app navigation until you visit
 * `?diag=0`. While on, it records a breadcrumb log — the navigation type of
 * each load, focus changes, JS errors, unhandled rejections, page-hide with the
 * focused element, and any app-initiated chunk reload (via `diagNote`) — and
 * paints them in a small fixed overlay you can screenshot. Tap the overlay to
 * clear the log.
 *
 * Reading the log after a reproduction:
 *  - a `chunk-guard reload: …` line  → the route-chunk guard reloaded us.
 *  - `load nav=reload` with no chunk line and no error → iOS reloaded the tab
 *    itself (e.g. memory pressure), not our code.
 *  - an `unhandledrejection: Importing a module script failed` line → a dynamic
 *    import failed.
 */

const ON_KEY = '__joms_diag_on'
const LOG_KEY = '__joms_diag_log'
const MAX = 60

type Entry = { t: number; m: string }

function isOn(): boolean {
  try {
    return localStorage.getItem(ON_KEY) === '1'
  } catch {
    return false
  }
}

function readLog(): Entry[] {
  try {
    const raw = localStorage.getItem(LOG_KEY)
    return raw ? (JSON.parse(raw) as Entry[]) : []
  } catch {
    return []
  }
}

function push(m: string) {
  try {
    const log = readLog()
    log.push({ t: Date.now(), m })
    while (log.length > MAX) log.shift()
    localStorage.setItem(LOG_KEY, JSON.stringify(log))
  } catch {
    /* storage unavailable (private mode / quota) — diagnostic is best-effort */
  }
}

/** Record a breadcrumb from elsewhere in the app (no-op unless diag is on). */
export function diagNote(m: string) {
  if (isOn()) push(m)
}

function describeEl(el: Element | null): string {
  if (!el) return 'none'
  const id = el.id ? `#${el.id}` : ''
  const name = (el as HTMLInputElement).name
    ? `[name=${(el as HTMLInputElement).name}]`
    : ''
  return `${el.tagName.toLowerCase()}${id}${name}`
}

function navType(): string {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    return nav?.type ?? 'unknown'
  } catch {
    return 'unknown'
  }
}

let overlayEl: HTMLElement | null = null

function render() {
  if (typeof document === 'undefined' || !document.body) return
  if (!overlayEl) {
    overlayEl = document.createElement('div')
    overlayEl.id = '__joms_diag_overlay'
    overlayEl.style.cssText = [
      'position:fixed',
      'left:0',
      'right:0',
      'bottom:0',
      'z-index:2147483647',
      'max-height:45vh',
      'overflow:auto',
      'background:rgba(0,0,0,0.92)',
      'color:#5dff8a',
      'font:11px/1.45 ui-monospace,monospace',
      'padding:8px 8px 14px',
      'white-space:pre-wrap',
      'border-top:2px solid #5dff8a',
    ].join(';')
    overlayEl.addEventListener('click', () => {
      try {
        localStorage.removeItem(LOG_KEY)
      } catch {
        /* ignore */
      }
      render()
    })
    document.body.appendChild(overlayEl)
  }
  const lines = readLog()
    .map((e) => {
      const d = new Date(e.t)
      const ts = `${String(d.getMinutes()).padStart(2, '0')}:${String(
        d.getSeconds(),
      ).padStart(2, '0')}.${String(d.getMilliseconds()).padStart(3, '0')}`
      return `${ts}  ${e.m}`
    })
    .join('\n')
  overlayEl.textContent = `RELOAD DIAG — tap to clear\n${lines || '(empty)'}`
}

/** Install once at app boot, before render. Safe to call unconditionally. */
export function installReloadDiag() {
  if (typeof window === 'undefined') return

  try {
    const q = new URLSearchParams(window.location.search).get('diag')
    if (q === '1') localStorage.setItem(ON_KEY, '1')
    if (q === '0') {
      localStorage.removeItem(ON_KEY)
      localStorage.removeItem(LOG_KEY)
    }
  } catch {
    /* ignore */
  }

  if (!isOn()) return

  push(`load nav=${navType()} path=${location.pathname}${location.search}`)

  window.addEventListener('error', (e) => push(`window.error: ${e.message}`))
  window.addEventListener('unhandledrejection', (e) => {
    const r = (e as PromiseRejectionEvent).reason
    push(`unhandledrejection: ${r instanceof Error ? r.message : String(r)}`)
  })
  window.addEventListener('pagehide', () =>
    push(`pagehide active=${describeEl(document.activeElement)}`),
  )
  window.addEventListener('beforeunload', () =>
    push(`beforeunload active=${describeEl(document.activeElement)}`),
  )
  document.addEventListener(
    'focusin',
    (e) => {
      push(`focusin ${describeEl(e.target as Element)}`)
      render()
    },
    true,
  )

  if (document.body) render()
  else window.addEventListener('DOMContentLoaded', render)
}
